import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import SalesList from './SalesList';
import BASEURL from '../API/Config';

const SalesOrder = () => {
  const [formData, setFormData] = useState({
    customerId: '',
    orderDate: '',
    items: [{ itemName: '', quantity: 0, price: 0 }],
    totalAmount: 0,
  });
  const [customers, setCustomers] = useState([]);
  const [showList, setShowList] = useState(false); // Toggle between form and list view
  const token = localStorage.getItem('token');
  const printRef = useRef();

  // Fetch Customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(`${BASEURL}/user/all-users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch customers');

        const data = await response.json();
        setCustomers(data);
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load customers.',
        });
      }
    };

    fetchCustomers();
  }, [token]);

  // Handle Form Inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = formData.items.map((item, i) =>
      i === index
        ? { ...item, [field]: field === 'quantity' || field === 'price' ? Number(value) : value }
        : item
    );
    setFormData((prevState) => ({ ...prevState, items: updatedItems }));
  };

  const handleAddItem = () => {
    setFormData((prevState) => ({
      ...prevState,
      items: [...prevState.items, { itemName: '', quantity: 0, price: 0 }],
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      items: prevState.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.price, 0);
  };

  // Print Invoice
  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload(); // Reload to restore original view
  };

  // Submit Sales Order
  const handleSubmit = async (e) => {
    e.preventDefault();

    const { customerId, orderDate, items } = formData;
    const totalAmount = calculateTotal();

    if (!customerId || !orderDate || items.length === 0 || totalAmount === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill in all required fields and add valid items.',
      });
      return;
    }

    try {
      const response = await fetch(`${BASEURL}/master/createsale`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...formData, totalAmount }),
      });

      if (!response.ok) throw new Error('Failed to create sales order');

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Sales order created successfully!',
      });

      // Reset form after submission
      setFormData({
        customerId: '',
        orderDate: '',
        items: [{ itemName: '', quantity: 0, price: 0 }],
        totalAmount: 0,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to create sales order.',
      });
    }
  };

  // Toggle View
  const handleToggleView = () => {
    setShowList(!showList);
  };

  return (
    <div className="container mt-5">
      <h3 className="mb-4 text-center text-primary">
        {showList ? 'Sales List' : 'Sales Order'}
      </h3>

      <button className="btn btn-primary mb-4" onClick={handleToggleView}>
        {showList ? 'Create Sales Order' : 'View Sales List'}
      </button>

      {!showList && (
        <form
          onSubmit={handleSubmit}
          className="p-4 shadow-sm rounded"
          style={{ backgroundColor: '#f8f9fa' }}
        >
          <div className="form-group mb-3">
            <label className="fw-semibold">Customer</label>
            <select
              name="customerId"
              className="form-control"
              value={formData.customerId}
              onChange={handleInputChange}
            >
              <option value="">Select Customer</option>
              {customers.map((customer) => (
                <option key={customer._id} value={customer._id}>
                  {customer.firstname} {customer.lastname}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-3">
            <label className="fw-semibold">Order Date</label>
            <input
              type="date"
              name="orderDate"
              className="form-control"
              value={formData.orderDate}
              onChange={handleInputChange}
            />
          </div>

          <div className="mb-4">
            <h5>Items</h5>
            {formData.items.map((item, index) => (
              <div key={index} className="row mb-3 align-items-end">
                <div className="col-md-4">
                  <label className="fw-semibold">Item Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={item.itemName}
                    onChange={(e) =>
                      handleItemChange(index, 'itemName', e.target.value)
                    }
                  />
                </div>
                <div className="col-md-3">
                  <label className="fw-semibold">Quantity</label>
                  <input
                    type="number"
                    className="form-control"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, 'quantity', e.target.value)
                    }
                  />
                </div>
                <div className="col-md-3">
                  <label className="fw-semibold">Price</label>
                  <input
                    type="number"
                    className="form-control"
                    value={item.price}
                    onChange={(e) =>
                      handleItemChange(index, 'price', e.target.value)
                    }
                  />
                </div>
                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleRemoveItem(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddItem}
            >
              Add Item
            </button>
          </div>

          <div className="form-group mb-3">
            <label className="fw-semibold">Total Amount</label>
            <input
              type="text"
              className="form-control"
              value={calculateTotal()}
              readOnly
            />
          </div>

          <div className="d-flex justify-content-center">
            <button type="submit" className="btn btn-success w-50">
              Submit
            </button>
            <button
              type="button"
              className="btn btn-primary w-50 ms-3"
              onClick={handlePrint}
            >
              Print Bill
            </button>
          </div>
        </form>
      )}

      {/* Printable Invoice */}
      <div
        ref={printRef}
        style={{
          display: 'none',
          padding: '20px',
          fontFamily: 'Arial, sans-serif',
          lineHeight: '1.5',
        }}
      >
        <h2 style={{ textAlign: 'center', marginBottom: '10px' }}>
          Sales Invoice
        </h2>
        <p style={{ textAlign: 'center', marginBottom: '20px' }}>
          Thank you for your business!
        </p>
        <hr />
        <p>
          <strong>Customer Name:</strong>{' '}
          {customers.find((c) => c._id === formData.customerId)?.firstname}{' '}
          {customers.find((c) => c._id === formData.customerId)?.lastname}
        </p>
        <p>
          <strong>Date:</strong> {formData.orderDate}
        </p>
        <hr />
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '20px',
            marginBottom: '20px',
          }}
        >
          <thead>
            <tr
              style={{ borderBottom: '2px solid #000', textAlign: 'left' }}
            >
              <th style={{ padding: '8px' }}>Item Name</th>
              <th style={{ padding: '8px' }}>Quantity</th>
              <th style={{ padding: '8px' }}>Price</th>
              <th style={{ padding: '8px' }}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {formData.items.map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: '8px' }}>{item.itemName}</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  {item.quantity}
                </td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  {item.price.toFixed(2)}
                </td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  {(item.quantity * item.price).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p
          style={{
            textAlign: 'right',
            fontSize: '18px',
            fontWeight: 'bold',
          }}
        >
          Total Amount: {calculateTotal().toFixed(2)}
        </p>
        <hr />
        <p
          style={{
            textAlign: 'center',
            marginTop: '20px',
          }}
        >
          Thank you for shopping with us!
          <br />
          We look forward to serving you again.
        </p>
      </div>

      {showList && <SalesList />}
    </div>
  );
};

export default SalesOrder;
